import { Card, Form, Row, Col, Button } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import Layout from "../../../layout/default";
import Block from "../../../components/Block/Block";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { Icon } from "../../../components";
import { useState, useEffect } from "react";
import api from "../../../../src/services/auth/api";

function ProductEdit() {
  const { id } = useParams();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [validated, setValidated] = useState(false);

  const navigate = useNavigate();

  // Handle input changes
  const handleInputs = (e) => {
    const { name, value, files } = e.target;

    if (files && files.length > 0) {
      // Handle file input
      setData({ ...data, [name]: files[0] });
    } else {
      setData({ ...data, [name]: value });
    }
  };

  // Fetch product data
  const getProductData = () => {
    api
      .get(`${process.env.REACT_APP_HOST}api/product/${id}`)
      .then((response) => {
        if (response.data.success) {
          setData(response.data.data);
        }
      })
      .catch((err) => {
        const message = err.response?.data?.errorMessages?.[0]?.message || "Something went wrong!";
        Swal.fire({
          icon: "error",
          title: message,
          text: "Unable to fetch product details.",
        }).then(() => navigate("/shop/admin/product-list"));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Update product data
  const postData = (event) => {
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.preventDefault();
      event.stopPropagation();
      setValidated(true);
      return;
    }

    event.preventDefault();

    // Create FormData and append all fields
    const formData = new FormData();
    Object.keys(data).forEach((key) => {
      if (key === "image_url" && data[key] instanceof File) {
        formData.append(key, data[key]); // Append file
      } else {
        formData.append(key, data[key]); // Append other fields
      }
    });

    // Append the product ID to the FormData
    formData.append("id", id);
    formData.delete("priority");

    // Make the POST request
    api
      .post(`${process.env.REACT_APP_HOST}api/products/update/${id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data", // Required for file uploads
        },
      })
      .then((response) => {
        if (response.data.success) {
          Swal.fire({
            icon: "success",
            title: "Product updated successfully",
          }).then(() => navigate("/shop/admin/product-list"));
        } else {
          updateError(response.data.content.error_description);
        }
      })
      .catch((err) => {
        if (err.response && err.response.data.validationErrors) {
          updateError(err.response.data.validationErrors);
        }
      });
  };

  const updateError = (message) => {
    let errorMessage;
    if (typeof message === "object") {
      errorMessage = Object.values(message).join("<br>");
    } else {
      errorMessage = message;
    }
    Swal.fire({
      icon: "error",
      title: "Save attempt was not successful",
      html: errorMessage,
    });
  };

  // Clear form data
  const clear = () => {
    setData({
      title: "",
      description: "",
      image_url: null, // Reset file input
    });
  };

  useEffect(() => {
    getProductData();
  }, [id]);

  return (
    <Layout title="Edit Product">
      <Block.Head>
        <Block.HeadBetween>
          <Block.HeadContent>
            <Block.Title tag="h2">Edit Product</Block.Title>
          </Block.HeadContent>
          <Block.HeadContent>
            <ul className="d-flex">
              <li>
                <Link
                  to="/shop/admin/product-list"
                  className="btn btn-primary btn-md d-md-none"
                >
                  <Icon name="arrow-long-left" />
                  <span>Go to List</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/shop/admin/product-list"
                  className="btn btn-primary d-none d-md-inline-flex"
                >
                  <Icon name="arrow-long-left" />
                  <span>Go to List</span>
                </Link>
              </li>
            </ul>
          </Block.HeadContent>
        </Block.HeadBetween>
      </Block.Head>

      <Block className="mt-n5">
        <Form noValidate validated={validated} onSubmit={postData}>
          <Row className="g-3">
            <Card>
              <Card.Body>
                {loading ? (
                  <h1 className="d-flex justify-content-center align-items-center">
                    Loading...
                  </h1>
                ) : (
                  <Row className="g-gs">
                    <Col lg="6">
                      <Form.Group className="form-group">
                        <Form.Label htmlFor="title">
                          Product Title<span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          id="title"
                          name="title"
                          value={data.title || ""}
                          onChange={handleInputs}
                          type="text"
                          placeholder="Enter Product Title"
                          required
                        />
                        <Form.Control.Feedback type="invalid">
                          Product Title is required.
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col lg="6">
                      <Form.Group className="form-group">
                        <Form.Label htmlFor="description">
                          Product Description<span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          id="description"
                          name="description"
                          value={data.description || ""}
                          onChange={handleInputs}
                          type="text"
                          placeholder="Enter Product Description"
                          required
                        />
                        <Form.Control.Feedback type="invalid">
                          Product Description is required.
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col lg="6">
                      <Form.Group className="form-group">
                        <Form.Label htmlFor="image_url">
                          Product image_url<span className="text-danger">*</span>
                        </Form.Label>
                        <div className="form-control-wrap">
                          <Form.Control
                            id="image_url"
                            name="image_url"
                            type="file"
                            onChange={handleInputs}
                            required
                          />
                          <Form.Control.Feedback type="invalid">
                            Product image_url is required.
                          </Form.Control.Feedback>
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                )}
              </Card.Body>
            </Card>

            <Card>
              <Card.Body>
                <div className="gap-col">
                  <ul className="d-flex align-items-center justify-content-center gap g-3">
                    <li>
                      <Button type="submit" variant="primary">
                        Update
                      </Button>
                    </li>
                    <li>
                      <Button type="button" variant="secondary" onClick={clear}>
                        Cancel
                      </Button>
                    </li>
                  </ul>
                </div>
              </Card.Body>
            </Card>
          </Row>
        </Form>
      </Block>
    </Layout>
  );
}

export default ProductEdit;
